import React, { createContext, useState, useEffect, useContext } from 'react';
import { authAPI } from '../api/auth.api';
import { setToken, removeToken, hasToken } from '../utils/token';

const AuthContext = createContext(null);
const LIMITED_PRODUCT_ORDER_ADMIN_EMAIL = 'zzvviiaadd@gmail.com';

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (hasToken()) {
      loadUser();
    } else {
      setLoading(false);
    }
  }, []);

  const loadUser = async () => {
    try {
      const data = await authAPI.getMe();
      setUser(data.user);
    } catch (error) {
      console.error('Failed to load user:', error);
      removeToken();
    } finally {
      setLoading(false);
    }
  };

  const login = async (credentials) => {
    const data = await authAPI.login(credentials);
    setToken(data.token);
    setUser(data.user);
    return data;
  };

  const register = async (userData) => {
    const data = await authAPI.register(userData);
    setToken(data.token);
    setUser(data.user);
    return data;
  };

  const logout = () => {
    removeToken();
    setUser(null);
  };

  const updateProfile = async (payload) => {
    const data = await authAPI.updateMe(payload);
    if (data.token) setToken(data.token);
    setUser(data.user);
    return data;
  };

  const normalizedEmail = String(user?.email || '').trim().toLowerCase();
  const isAnyAdmin = user?.role === 'admin';
  const isLimitedAdmin = isAnyAdmin && normalizedEmail === LIMITED_PRODUCT_ORDER_ADMIN_EMAIL;
  const isFullAdmin = isAnyAdmin && !isLimitedAdmin;

  const value = {
    user,
    loading,
    login,
    register,
    logout,
    updateProfile,
    isAuthenticated: !!user,
    isAdmin: isFullAdmin,
    isFullAdmin,
    isLimitedAdmin,
    isCoworker: user?.role === 'coworker',
    canManageProducts: isAnyAdmin,
    canManageOrders: isAnyAdmin || user?.role === 'coworker',
    canManageOrderAdminActions: isAnyAdmin,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;
